// The lesson's facts (SPEC §7.4 header).

import type { Lesson } from "../../api/types";
import { formatLongDate, t, translateCode } from "../../i18n/i18n";
import { formatCell, formatRange } from "../../time";
import { appendChildren, createBadge, createElement, createLink } from "../../ui/dom";
import { describeDataProblems, describeLessonFlags } from "../../ui/lessonLabels";

/** Group, course, date, time, teacher(s), room, flags, notes and data problems. */
export function buildLessonHeader(lesson: Lesson): HTMLElement {
  const card: HTMLDivElement = createElement("div", "card");
  const facts: HTMLDListElement = createElement("dl", "lesson-header");
  appendFact(facts, t("lesson.group"), createLink(`/groups/${lesson.key.groupId}`, lesson.groupName));
  appendFact(facts, t("lesson.course"), lesson.courseName);
  appendFact(facts, t("app.date"), formatLongDate(lesson.key.date));
  appendFact(facts, t("app.time"), describeTime(lesson));
  appendFact(facts, t("lesson.teacher"), createLink(`/teachers/${lesson.teacherId}?from=${lesson.key.date}`,
    lesson.teacherName));
  if (lesson.isSubstituted) {
    appendFact(facts, t("lesson.mainTeacher"), lesson.mainTeacherName);
  }

  appendFact(facts, t("lesson.room"), `${lesson.classroomName}, ${lesson.centerName}`);
  appendOptionalFact(facts, t("lesson.note"), lesson.note);
  appendOptionalFact(facts, t("lesson.teacherNote"), lesson.teacherNote);
  card.append(facts, buildFlags(lesson));
  return card;
}

function describeTime(lesson: Lesson): string {
  const time: string = formatRange(lesson.range);
  if (lesson.isMoved === false) {
    return time;
  }

  return `${time} (${t("lesson.movedFrom", { time: formatCell(lesson.plannedRange.start) })})`;
}

function buildFlags(lesson: Lesson): HTMLElement {
  const flags: HTMLDivElement = createElement("div");
  for (const label of describeLessonFlags(lesson)) {
    flags.append(createBadge(label, "neutral"));
  }

  for (const problem of describeDataProblems(lesson)) {
    flags.append(createBadge(problem, "negative"));
  }

  if (lesson.isCenterClosed && lesson.closureReason !== null) {
    flags.append(createBadge(translateCode("closureReasons", lesson.closureReason), "warning"));
  }

  return flags;
}

function appendFact(list: HTMLDListElement, label: string, value: string | HTMLElement): void {
  const term: HTMLElement = createElement("dt", "field-label", label);
  const description: HTMLElement = createElement("dd");
  appendChildren(description, typeof value === "string" ? document.createTextNode(value) : value);
  const pair: HTMLDivElement = createElement("div");
  pair.append(term, description);
  list.append(pair);
}

function appendOptionalFact(list: HTMLDListElement, label: string, value: string | null): void {
  if (value !== null) {
    appendFact(list, label, value);
  }
}
